// Talks to the world server: guest creation, the WebSocket view stream and commands.
import { signal } from '@preact/signals';
import type { Command, Faction, Persona } from '@aurane/protocol';
import type { ForgedSite, play, rt, Silence, team } from '@aurane/forge';
import type { ApplyResult, BattleReport, CapitalOffer, PlayerView, SystemDetailView } from '@aurane/sim';
import { tError } from './i18n/index.js';
import { remedyFor } from './ui/remedy.js';
import { teachEvent, teachKey } from './ui/teach.js';

export const view = signal<PlayerView | null>(null);
export const status = signal<'idle' | 'connecting' | 'online' | 'offline'>('idle');
export const toast = signal<{ text: string; kind: 'ok' | 'err' } | null>(null);

const TOKEN_KEY = 'aurane.token';
export const getToken = (): string | null => { try { return localStorage.getItem(TOKEN_KEY); } catch { return null; } };
const setToken = (t: string): void => { try { localStorage.setItem(TOKEN_KEY, t); } catch { /* ignore */ } };
export const forget = (): void => { try { localStorage.removeItem(TOKEN_KEY); } catch { /* ignore */ } };

/** The founding choice (issue #47): three capitals and the one the General recommends, null when offline. */
export async function fetchCapitals(faction: Faction, persona: Persona): Promise<CapitalOffer | null> {
  try { const r = await fetch(`/api/capitals?faction=${faction}&persona=${persona}`); if (r.ok) return await r.json() as CapitalOffer; } catch { /* offline */ }
  return null;
}

/** Founds a Colony, or (a code used in the last days by a guest who lost their session) reopens it: `resumed`. */
export async function createGuest(name: string, faction: Faction, persona: Persona, invite?: string, capital?: string): Promise<{ resumed: boolean; name: string | null }> {
  const res = await fetch('/api/guest', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ name, faction, persona, ...(invite ? { invite } : {}), ...(capital ? { capital } : {}) }) });
  if (!res.ok) throw new Error((await res.json() as { error: string }).error);
  const r = await res.json() as { token: string; resumed?: boolean; name?: string };
  setToken(r.token);
  return { resumed: r.resumed === true, name: r.name ?? null };
}

/** Payments (decision 0011): enabled only when the server has Stripe configured; the SKUs on sale, price in CHF. */
export interface PaymentsConfig { enabled: boolean; skus: { id: string; priceChf: number }[] }
/** `expeditions`: the Forge's wrecks can be explored (decision 0015); off, the routes answer 404 `expeditions_disabled`. */
export interface PublicConfig { requireInvite: boolean; seasonDays: number; seasonSeed: string; payments?: PaymentsConfig; expeditions?: boolean; /** New expeditions are the real-time dungeon (rules 7). */ expeditionRt?: boolean; /** The deck in 3D (the Forge's Babylon.js viewer at /forge/). */ expedition3d?: boolean }
export async function fetchPublicConfig(): Promise<PublicConfig> {
  try { const r = await fetch('/api/public/config'); if (r.ok) return await r.json() as PublicConfig; } catch { /* offline */ }
  return { requireInvite: false, seasonDays: 56, seasonSeed: '' };
}

/** Back from Stripe's hosted page: `?paid=<sku>` (success) or `?paid=cancel`. Read once, then removed from the URL. */
function readPaidReturn(): { sku: string } | 'cancel' | null {
  try {
    const q = new URLSearchParams(location.search);
    const paid = q.get('paid');
    if (!paid) return null;
    q.delete('paid');
    const rest = q.toString();
    history.replaceState(null, '', location.pathname + (rest ? `?${rest}` : '') + location.hash);
    return paid === 'cancel' ? 'cancel' : /^[a-z0-9_]{1,40}$/.test(paid) ? { sku: paid } : null;
  } catch { return null; }
}
export const paidReturn = signal<{ sku: string } | 'cancel' | null>(readPaidReturn());

export interface Entitlement { sku: string; createdAt: number; expiresAt: number | null; active: boolean }
export async function fetchEntitlements(): Promise<{ payments: boolean; account: boolean; entitlements: Entitlement[] } | null> {
  try { const res = await fetch('/api/account/entitlements', { headers: authHeaders() }); return res.ok ? await res.json() as { payments: boolean; account: boolean; entitlements: Entitlement[] } : null; } catch { return null; }
}
/** Opens Stripe's hosted checkout for this SKU; resolves only on failure (on success the page navigates away). */
export async function startCheckout(sku: string, l: 'fr' | 'en'): Promise<{ ok: false; reason: string }> {
  try {
    const res = await fetch('/api/pay/checkout', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ sku, lang: l }) });
    const body = await res.json().catch(() => ({})) as { url?: string; error?: string };
    if (res.ok && body.url) { location.assign(body.url); return await new Promise<never>(() => { /* navigating */ }); }
    return { ok: false, reason: body.error ?? `http ${res.status}` };
  } catch { return { ok: false, reason: 'network' }; }
}

/** Opens an existing colony on this device from a link code (see requestLink). */
export async function redeem(code: string): Promise<void> {
  const res = await fetch('/api/redeem', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ code }) });
  if (!res.ok) throw new Error((await res.json() as { error: string }).error);
  const { token } = await res.json() as { token: string };
  setToken(token);
}

/** A 24 h link that opens this colony on another device. */
export async function requestLink(): Promise<{ code: string; url: string } | null> {
  const token = getToken();
  if (!token) return null;
  const res = await fetch('/api/link', { method: 'POST', headers: { authorization: `Bearer ${token}` } });
  return res.ok ? await res.json() as { code: string; url: string } : null;
}

/** `#join=<code>` in the URL: a device link pasted or scanned. */
export function joinCodeFromUrl(): string | null {
  const m = /[#&]join=([^&]+)/.exec(location.hash);
  return m ? decodeURIComponent(m[1]!) : null;
}

let ws: WebSocket | null = null;
let pending = new Map<string, (r: ApplyResult) => void>();
let seq = 0;
let retry = 1000;

export function connect(): void {
  const token = getToken();
  if (!token) return;
  status.value = 'connecting';
  // A token from a past season (or a wiped server) is refused at the upgrade with a plain 401 the socket cannot see:
  // probe once over HTTP and go back to the landing page instead of retrying forever.
  void fetch('/api/me', { headers: { authorization: `Bearer ${token}` } })
    .then((r) => { if (r.status === 401) { forget(); view.value = null; status.value = 'idle'; return; } openSocket(token); })
    .catch(() => openSocket(token));
}

function openSocket(token: string): void {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  ws = new WebSocket(`${proto}://${location.host}/ws?token=${encodeURIComponent(token)}`);
  ws.onopen = () => { status.value = 'online'; retry = 1000; if (watched) ws?.send(JSON.stringify({ watch: watched })); };
  ws.onmessage = (ev) => {
    const msg = JSON.parse(ev.data as string) as { type: 'view'; view: PlayerView } | { type: 'system'; view: SystemDetailView } | { type: 'result'; id?: string; result: ApplyResult } | { type: 'error'; error: string };
    if (msg.type === 'view') view.value = msg.view;
    else if (msg.type === 'system') { if (msg.view.id === watched && !frozen) systemView.value = msg.view; }
    else if (msg.type === 'result' && msg.id) { pending.get(msg.id)?.(msg.result); pending.delete(msg.id); }
    else if (msg.type === 'error') toast.value = { text: msg.error, kind: 'err' };
  };
  ws.onclose = (ev) => {
    status.value = 'offline';
    for (const fn of pending.values()) fn({ ok: false, reason: 'offline' });
    pending = new Map();
    if (ev.code === 1008 || ev.code === 4401) { forget(); view.value = null; status.value = 'idle'; return; }
    setTimeout(connect, retry);
    retry = Math.min(retry * 2, 15000);
  };
}

/** The plateau currently streamed at 2 Hz (System view), or null. */
export const systemView = signal<SystemDetailView | null>(null);
let watched: string | null = null;
export function watch(systemId: string | null): void {
  watched = systemId;
  if (!systemId) systemView.value = null;
  if (ws && ws.readyState === WebSocket.OPEN) ws.send(JSON.stringify({ watch: systemId }));
}

export function send(command: Command): Promise<ApplyResult> {
  return new Promise((resolve) => {
    if (!ws || ws.readyState !== WebSocket.OPEN) { resolve({ ok: false, reason: 'offline' }); return; }
    const id = String(++seq);
    pending.set(id, resolve);
    ws.send(JSON.stringify({ id, command }));
  });
}

export async function act(command: Command, okText?: string): Promise<boolean> {
  const r = await send(command);
  // The General's « Here: … » hint for this very command is over once it is sent (issue #39).
  if (r.ok) { teachEvent({ kind: 'command', key: teachKey(command) }); if (okText) toast.value = { text: okText, kind: 'ok' }; }
  else {
    // The refusal and its remedy (issue #47): long enough on screen to be read.
    const remedy = remedyFor(r.reason, command, view.value);
    const text = remedy ? `${tError(r.reason)}. ${remedy}` : tError(r.reason);
    toast.value = { text, kind: 'err' };
    setTimeout(() => { if (toast.value?.text === text) toast.value = null; }, remedy ? 6000 : 2500);
    return false;
  }
  setTimeout(() => { toast.value = null; }, 2500);
  return r.ok;
}

export const authHeaders = (): Record<string, string> => ({ authorization: `Bearer ${getToken() ?? ''}`, 'content-type': 'application/json' });

export async function fetchBriefing(lang: 'fr' | 'en'): Promise<{ text: string; source: string; awaySeconds: number; draws?: number } | null> {
  const res = await fetch(`/api/briefing?lang=${lang}`, { headers: authHeaders() });
  return res.ok ? (await res.json() as { text: string; source: string; awaySeconds: number; draws?: number }) : null;
}

/** `doctrine`: the General's answer to an order; the thread shows « Doctrine reçue — voici ma lecture » before it. */
export interface Turn { who: 'me' | 'general'; text: string; at: number; doctrine?: true }
/** Talk to the General: small talk, questions, orders. */
export interface TalkAnswer { reply: string; source: string; policyChanged: boolean; history: Turn[]; /** A doctrine waiting for the player's yes (DOCTRINE_CONFIRM). */ pending?: { id: string; readable: string[] } | null; question?: string | null }
export async function talk(text: string, lang: 'fr' | 'en'): Promise<TalkAnswer | null> {
  try {
    const res = await fetch('/api/talk', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ text, lang }) });
    return res.ok ? (await res.json() as TalkAnswer) : null;
  } catch { return null; }
}
/** A card of the Draw Counsel in the General's voice (LLM layer, decision 0009). */
export interface CounselCard { id: string; title: string; line: string; command: Command | null; show: { screen: 'galaxy' | 'system' | 'colony' | 'market' | 'general' | 'journal'; system?: string; poi?: string; slot?: string } | null; /** The simulation's own target when the card came from its Counsel. */ raw?: unknown }
export interface CounselView { drawIndex: number; minutesToDraw: number; cards: CounselCard[]; source: string; writtenAt: number; /** Onboarding tier it was written for: a new tier rewrites it. */ tier?: number }

export async function fetchCounsel(lang: 'fr' | 'en'): Promise<CounselView | null> {
  try { const res = await fetch(`/api/counsel?lang=${lang}`, { headers: authHeaders() }); return res.ok ? (await res.json() as CounselView) : null; } catch { return null; }
}

/** "Do it" / "Not now" on a voice card: the world runs the command and the General remembers the choice. */
export async function answerCounsel(id: string, take: boolean): Promise<{ ok: boolean; reply: string | null; gone?: boolean }> {
  try {
    const res = await fetch(`/api/counsel/${take ? 'take' : 'skip'}`, { method: 'POST', headers: authHeaders(), body: JSON.stringify({ id }) });
    // 404: the card already left the Counsel (done by hand); 409 stale: its goal is reached or no longer on the table.
    if (res.status === 404 || res.status === 409) {
      const body = await res.json().catch(() => ({})) as { reason?: string };
      if (res.status === 404 || body.reason === 'stale') return { ok: false, reply: null, gone: true };
      toast.value = { text: tError(body.reason ?? ''), kind: 'err' }; setTimeout(() => { toast.value = null; }, 2500);
      return { ok: false, reply: null };
    }
    if (!res.ok) return { ok: false, reply: null };
    const body = await res.json() as { ok?: boolean; reply?: string; result?: { ok?: boolean; reason?: string } };
    if (body.result && body.result.ok === false) { toast.value = { text: tError(body.result.reason ?? ''), kind: 'err' }; setTimeout(() => { toast.value = null; }, 2500); return { ok: false, reply: null }; }
    return { ok: body.ok !== false, reply: body.reply ?? null };
  } catch { return { ok: false, reply: null }; }
}

export async function fetchTalk(): Promise<Turn[]> {
  try { const res = await fetch('/api/talk', { headers: authHeaders() }); return res.ok ? ((await res.json() as { history: Turn[] }).history) : []; } catch { return []; }
}

/** The Archives of my General (decision 0012): memories found again, and per arc how many are still lost. */
export interface SouvenirView { id: string; arc: number; order: number; title: { fr: string; en: string }; text: { fr: string; en: string }; at: number }
export interface ArchivesView { arcs: { arc: number; found: number; total: number }[]; found: SouvenirView[]; echoes?: number; hints?: { id: string; arc: number; hint: { fr: string; en: string }; places?: string[] }[]; seance?: boolean; /** Corthexis: counsels taken (the choices layer, bounded). */ counselTaken?: number; /** Corthexis: what the last expeditions cost (newest first). */ expeditions?: { system: string; name: string; lost: number; home: number; won: boolean; preset: 'light' | 'standard' | 'heavy' | null; at: number; /** Note v2 (absent on older notes): scenario id, difficulty, loot credited by resource, fallen by role (never names), counsel. */ scenario?: string; difficulty?: 'normal' | 'hard'; loot?: Record<string, number>; fallen?: Record<string, number>; counsel?: 'taken' | 'ignored' }[] }
/** A Séance (decision 0013): the memory opens fragment by fragment, the world decides, the General tells. */
export interface SeanceView { system: string; systemName: string; turn: number; open: number; done: boolean; outcome: 'remembered' | 'hint' | null; history: { who: 'me' | 'general'; text: string }[]; turns: number }
async function seancePost(path: string, body: unknown): Promise<{ ok: boolean; seance?: SeanceView; reason?: string }> {
  try { const res = await fetch(path, { method: 'POST', headers: { ...authHeaders(), 'content-type': 'application/json' }, body: JSON.stringify(body) }); return await res.json() as { ok: boolean; seance?: SeanceView; reason?: string }; } catch { return { ok: false, reason: 'offline' }; }
}
export const openSeance = (system: string, lang: 'fr' | 'en') => seancePost('/api/seance/open', { system, lang });
export const sayInSeance = (text: string) => seancePost('/api/seance/say', { text });
export const closeSeance = () => seancePost('/api/seance/close', {});
/** Decision 0014, the Table: the next Draws on a copy of the world, figures as [low, high] over two fictitious draws. */
export interface ProjectionView { at: number; draws: { index: number; stock: Record<string, [number, number]>; score: [number, number]; connected: number; dark: number }[] }
export async function fetchProjection(): Promise<ProjectionView | null> {
  try { const res = await fetch('/api/projection?draws=3', { headers: authHeaders() }); return res.ok ? await res.json() as ProjectionView : null; } catch { return null; }
}
export async function fetchSeance(): Promise<SeanceView | null> {
  try { const res = await fetch('/api/seance', { headers: authHeaders() }); return res.ok ? ((await res.json()) as { seance: SeanceView | null }).seance : null; } catch { return null; }
}
export async function fetchArchives(): Promise<ArchivesView | null> {
  try { const res = await fetch('/api/souvenirs', { headers: authHeaders() }); return res.ok ? await res.json() as ArchivesView : null; } catch { return null; }
}

export interface DoctrineAnswer { summary: string; source: string; warnings: string[]; reply: string; readable?: string[]; question?: string | null; pending?: { id: string } | null; applied?: boolean }
export async function submitDoctrine(text: string, lang: 'fr' | 'en'): Promise<DoctrineAnswer | null> {
  try {
    const res = await fetch('/api/doctrine', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ text, lang }) });
    return res.ok ? (await res.json() as DoctrineAnswer) : null;
  } catch { return null; }
}

/** The doctrine waiting for the player's yes, as the server keeps it (null when none, or on a network error). */
export async function fetchPendingDoctrine(): Promise<unknown> {
  try { const res = await fetch('/api/doctrine/pending', { headers: authHeaders() }); return res.ok ? await res.json() : null; } catch { return null; }
}

/** "Apply": the pending doctrine becomes the Colony's. `gone` when it was replaced or expired meanwhile. */
export async function confirmDoctrine(id: string): Promise<'ok' | 'gone' | 'offline'> {
  try {
    const res = await fetch('/api/doctrine/confirm', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ id }) });
    if (res.status === 404) return 'gone';
    return res.ok ? 'ok' : 'offline';
  } catch { return 'offline'; }
}

/** "Not like that": the pending doctrine is dropped, the active one stays. */
export async function discardDoctrine(): Promise<boolean> {
  try { return (await fetch('/api/doctrine/discard', { method: 'POST', headers: authHeaders(), body: '{}' })).ok; } catch { return false; }
}

export type BattleSummary = { id: string; system: string; systemName: string; poi: string; startedAt: number; endedAt: number | null; sides: string[]; kills: number };
export async function fetchBattles(): Promise<BattleSummary[]> {
  const res = await fetch('/api/battles', { headers: authHeaders() });
  return res.ok ? (await res.json() as BattleSummary[]) : [];
}
export async function fetchBattle(id: string): Promise<BattleReport | null> {
  const res = await fetch(`/api/battle/${encodeURIComponent(id)}`, { headers: authHeaders() });
  return res.ok ? (await res.json() as BattleReport) : null;
}

// Debug hook for visual checks: open the app with #debug, freeze the stream and set a System frame by hand.
let frozen = false;
if (typeof location !== 'undefined' && location.hash === '#debug') (globalThis as unknown as { __aurane: unknown }).__aurane = { systemView, view, freeze: (on: boolean) => { frozen = on; } };

// --- sessions (decision 0010, lot A): the devices holding this colony, leave, cut one off ------------------

export interface SessionInfo { id: string; label: string; createdAt: number; lastSeenAt: number | null; current: boolean }

export async function fetchSessions(): Promise<SessionInfo[]> {
  try { const res = await fetch('/api/sessions', { headers: authHeaders() }); return res.ok ? ((await res.json() as { sessions: SessionInfo[] }).sessions) : []; } catch { return []; }
}

export async function revokeSession(id: string): Promise<boolean> {
  try { return (await fetch(`/api/sessions/${encodeURIComponent(id)}`, { method: 'DELETE', headers: authHeaders() })).ok; } catch { return false; }
}

/** Ends this device's session on the server, forgets the token and returns to the landing page. */
export async function logout(): Promise<void> {
  try { await fetch('/api/session', { method: 'DELETE', headers: authHeaders() }); } catch { /* the token is dropped anyway */ }
  forget();
  const sock = ws; ws = null;
  try { sock?.close(1000, 'logout'); } catch { /* ignore */ }
  view.value = null;
  status.value = 'idle';
}

/** The General's memory of this player, as a file to keep or read; null when the endpoint is not there. */
export async function exportMemory(): Promise<unknown | null> {
  try { const res = await fetch('/api/memory', { headers: authHeaders() }); return res.ok ? await res.json() : null; } catch { return null; }
}

export async function eraseMemory(): Promise<boolean> {
  try { return (await fetch('/api/memory', { method: 'DELETE', headers: authHeaders() })).ok; } catch { return false; }
}

// --- passkeys (decision 0010, lot B) ------------------------------------------------------------------------

export interface AccountInfo { account: { id: string; createdAt: number; email: string | null; notifyEmail?: boolean } | null; passkeys: { id: string; label: string; createdAt: number; lastUsedAt: number | null }[] }

export async function fetchAccount(): Promise<AccountInfo | null> {
  try { const res = await fetch('/api/account', { headers: authHeaders() }); return res.ok ? await res.json() as AccountInfo : null; } catch { return null; }
}

export function passkeysSupported(): boolean {
  return typeof window !== 'undefined' && 'PublicKeyCredential' in window && typeof navigator.credentials?.create === 'function';
}

/** Adds a passkey to this colony's account (created on the spot): one tap on the device's own unlock. */
export async function addPasskey(lang: 'fr' | 'en'): Promise<{ ok: boolean; reason?: string }> {
  try {
    const { startRegistration } = await import('@simplewebauthn/browser');
    const optRes = await fetch(`/api/auth/passkey/register/options?lang=${lang}`, { method: 'POST', headers: authHeaders() });
    if (!optRes.ok) return { ok: false, reason: 'options' };
    const optionsJSON = await optRes.json();
    const response = await startRegistration({ optionsJSON });
    const res = await fetch('/api/auth/passkey/register/verify', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ response }) });
    if (!res.ok) return { ok: false, reason: ((await res.json().catch(() => ({}))) as { error?: string }).error ?? 'verify' };
    return { ok: true };
  } catch (err) { return { ok: false, reason: (err as Error).name === 'NotAllowedError' ? 'cancelled' : (err as Error).message }; }
}

/** A returning account with no colony this season: a single-use ticket to found one, last season's colony to prefill the
 *  form, and whether its General remembers it. */
export interface FoundOffer { ticket: string; expiresAt: number; previous: { name: string; faction: Faction; persona: Persona } | null; remembers: boolean }
export interface SignInResult { ok: boolean; reason?: string; found?: FoundOffer }

/** Founds this season's colony of a returning account (no invitation); the device session is kept like a guest's. */
export async function foundColony(ticket: string, name: string, faction: Faction, persona: Persona, capital?: string): Promise<void> {
  const res = await fetch('/api/account/found', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ ticket, name, faction, persona, ...(capital ? { capital } : {}) }) });
  const body = await res.json().catch(() => ({})) as { token?: string; error?: string };
  if (!res.ok || !body.token) throw new Error(body.error ?? 'network');
  setToken(body.token);
}

/** Signs in with a passkey from the landing page: the account's colony opens here with a fresh session. */
export async function loginWithPasskey(): Promise<SignInResult> {
  try {
    const { startAuthentication } = await import('@simplewebauthn/browser');
    const optRes = await fetch('/api/auth/passkey/login/options', { method: 'POST' });
    if (!optRes.ok) return { ok: false, reason: 'options' };
    const { handle, options } = await optRes.json() as { handle: string; options: Parameters<typeof startAuthentication>[0]['optionsJSON'] };
    const response = await startAuthentication({ optionsJSON: options });
    const res = await fetch('/api/auth/passkey/login/verify', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ handle, response }) });
    const body = await res.json().catch(() => ({})) as { token?: string; error?: string; found?: FoundOffer };
    if (!res.ok || !body.token) return { ok: false, reason: body.error ?? 'verify', ...(body.found ? { found: body.found } : {}) };
    setToken(body.token);
    return { ok: true };
  } catch (err) { return { ok: false, reason: (err as Error).name === 'NotAllowedError' ? 'cancelled' : (err as Error).message }; }
}

export async function removePasskey(id: string): Promise<boolean> {
  try { return (await fetch(`/api/account/passkeys/${encodeURIComponent(id)}`, { method: 'DELETE', headers: authHeaders() })).ok; } catch { return false; }
}


// --- e-mail codes (decision 0010, lot C) ---------------------------------------------------------------------

/** Asks for a code at this address; the account is created on the spot when the colony has none. */
export async function startEmail(email: string, lang: 'fr' | 'en'): Promise<{ ok: boolean; reason?: string }> {
  try {
    const res = await fetch('/api/account/email/start', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ email, lang }) });
    if (res.ok) return { ok: true };
    return { ok: false, reason: ((await res.json().catch(() => ({}))) as { error?: string }).error ?? 'start' };
  } catch (err) { return { ok: false, reason: (err as Error).message }; }
}

export async function verifyEmail(code: string): Promise<{ ok: boolean; reason?: string }> {
  try {
    const res = await fetch('/api/account/email/verify', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ code }) });
    if (res.ok) return { ok: true };
    return { ok: false, reason: ((await res.json().catch(() => ({}))) as { error?: string }).error ?? 'verify' };
  } catch (err) { return { ok: false, reason: (err as Error).message }; }
}

/** The return e-mail (issue #48): the player's yes or no. */
export async function setNotifyEmail(on: boolean): Promise<boolean> {
  try { return (await fetch('/api/account/notify', { method: 'POST', headers: { ...authHeaders(), 'content-type': 'application/json' }, body: JSON.stringify({ on }) })).ok; } catch { return false; }
}

/** Web Push (issue #48): on in production only with the server's key. */
export async function fetchPushConfig(): Promise<{ enabled: boolean; publicKey: string | null }> {
  try { const r = await fetch('/api/push/config'); return r.ok ? await r.json() as { enabled: boolean; publicKey: string | null } : { enabled: false, publicKey: null }; } catch { return { enabled: false, publicKey: null }; }
}

export async function removeEmail(): Promise<boolean> {
  try { return (await fetch('/api/account/email', { method: 'DELETE', headers: authHeaders() })).ok; } catch { return false; }
}

/** Sign in by e-mail from the landing page: a handle now, the code by mail, the colony once it is typed. */
export async function startEmailLogin(email: string, lang: 'fr' | 'en'): Promise<string | null> {
  try {
    const res = await fetch('/api/auth/email/start', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ email, lang }) });
    return res.ok ? ((await res.json()) as { handle: string }).handle : null;
  } catch { return null; }
}

export async function verifyEmailLogin(handle: string, code: string): Promise<SignInResult> {
  try {
    const res = await fetch('/api/auth/email/verify', { method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({ handle, code }) });
    const body = await res.json().catch(() => ({})) as { token?: string; error?: string; found?: FoundOffer };
    if (!res.ok || !body.token) return { ok: false, reason: body.error ?? 'verify', ...(body.found ? { found: body.found } : {}) };
    setToken(body.token);
    return { ok: true };
  } catch (err) { return { ok: false, reason: (err as Error).message }; }
}

/** What an ended rules-7 game gave back to the Colony (troops by type, resources, material extracted). */
export interface ExpeditionSettlement { troopsHome: Partial<Record<rt.UnitType, number>>; troopsLost: Partial<Record<rt.UnitType, number>>; refund: Record<string, number>; loot: Record<string, number>; extracted: number }
/** An expedition into a forged wreck (decision 0015): the site and the actions done, in order; the server replays them. */
export interface Expedition {
  id: string; system: string; poi: string; difficulty: 'normal' | 'hard'; hull: number;
  site: ForgedSite; done: string[]; outcome: play.Outcome | team.Outcome | rt.RtOutcome; startedAt: number; updatedAt: number;
  /** Rules 7 (the real-time dungeon): the setup, the orders the server kept (replay them to rebuild the game: `rt.replay`),
   *  the tick reached, the summary to draw, and the latest tick the wall clock allows (`maxTick`). `done` is empty. */
  rt?: { setup: rt.Setup; orders: rt.RtOrder[]; tick: number; view: rt.RtView; maxTick: number; /** Once the game is over: what came home and what was lost. */ settlement?: ExpeditionSettlement };
  /** 1: one team that never walks (`done` = actions); 2: four units with roles (`done` = orders), with its Silence;
   *  3: the same, won when the core is back at the drop. */
  rules?: 1 | 2 | 3 | 4 | 5 | 6 | 7; silence?: Silence;
  /** When the team is back on its feet, once the game is over (AURANE_EXPEDITION_REST). */
  restUntil?: number;
}
/** The server's answer: the expedition, or its refusal (`not_a_wreck`, `already_active`, `illegal_action`, `finished`,
 *  `expeditions_disabled`…; `offline` when it could not be reached). */
export type ExpeditionReply = { ok: true; exp: Expedition } | { ok: false; error: string; details?: ExpeditionErrorDetails };
/** What a refusal says besides its code: the order and the rule that refused it (`illegal_order`), the latest tick the wall
 *  clock allows (`too_fast`), what cannot embark (`bad_loadout`), what the Colony lacks (`insufficient`). */
export interface ExpeditionErrorDetails {
  index?: number; why?: string; maxTick?: number; problems?: string[];
  missing?: Partial<Record<'metal' | 'energy' | 'food' | 'crystal' | 'rium', number>>; troops?: Partial<Record<rt.UnitType, number>>; restUntil?: number;
}

async function expeditionCall(path: string, body?: unknown): Promise<ExpeditionReply> {
  try {
    const res = await fetch(`/api/expedition${path}`, body === undefined ? { headers: authHeaders() } : { method: 'POST', headers: authHeaders(), body: JSON.stringify(body) });
    const j = await res.json().catch(() => ({})) as ExpeditionErrorDetails & { error?: string } & Partial<Expedition>;
    if (res.ok && j.id) return { ok: true, exp: j as Expedition };
    const details: ExpeditionErrorDetails = {};
    if (typeof j.index === 'number') details.index = j.index;
    if (typeof j.why === 'string') details.why = j.why;
    if (typeof j.maxTick === 'number') details.maxTick = j.maxTick;
    if (Array.isArray(j.problems)) details.problems = j.problems;
    if (j.missing && typeof j.missing === 'object') details.missing = j.missing;
    if (j.troops && typeof j.troops === 'object') details.troops = j.troops;
    if (typeof j.restUntil === 'number') details.restUntil = j.restUntil;
    return { ok: false, error: j.error ?? `http_${res.status}`, ...(Object.keys(details).length ? { details } : {}) };
  } catch { return { ok: false, error: 'offline' }; }
}
/** The expedition in progress, if any, and until when the team rests; null when the server cannot be reached. */
export async function fetchExpeditionState(): Promise<{ active: Expedition | null; restUntil: number | null } | null> {
  try {
    const res = await fetch('/api/expedition', { headers: authHeaders() });
    if (!res.ok) return null;
    const j = (await res.json()) as { active: Expedition | null; restUntil?: number | null };
    return { active: j.active, restUntil: j.restUntil ?? null };
  } catch { return null; }
}
/** The expedition in progress, if any; null also when the server cannot be reached. */
export async function fetchExpedition(): Promise<Expedition | null> {
  return (await fetchExpeditionState())?.active ?? null;
}
/** Rules 7 also take the loadout the team leaves with: the units, the kit, and the freight pods added to the shuttle
 *  (`rt.loadoutProblems` says what cannot embark: 400 `bad_loadout` with `problems`; 409 `insufficient` with what the
 *  Colony lacks). What the wreck holds is the site's own: `scenario` is honoured only by a server set for tests. */
export interface RtStartOptions { scenario?: rt.ScenarioKind; loadout?: Pick<rt.Loadout, 'units' | 'modules' | 'ammo' | 'kits' | 'material'> & { pods?: number } }
export const startExpedition = (system: string, poi: string, difficulty?: 'normal' | 'hard', opts: RtStartOptions = {}): Promise<ExpeditionReply> =>
  expeditionCall('/start', { system, poi, ...(difficulty ? { difficulty } : {}), ...opts });
/** Rules 7: a batch of stamped orders (one every 1-2 s, not one per click) and the client's current tick; with no orders
 *  it is a heartbeat. The answer is the expedition (`rt.view`), or a refusal: `past_tick`, `too_fast` (`maxTick`),
 *  `illegal_order` (`index`, `why`), `too_many_orders`, `finished`, `not_realtime`. A refused batch changes nothing. */
export const expeditionOrders = (orders: rt.RtOrder[], tick?: number): Promise<ExpeditionReply> =>
  expeditionCall('/orders', { orders, ...(tick !== undefined ? { tick } : {}) });
/** The Colony's troops and queue, the capital's stock and what things cost (rules 7: the preparation screen). */
export interface ExpeditionStock {
  troops: Partial<Record<rt.UnitType, number>>; queue: { unit: rt.UnitType; count: number; readyAt: number }[]; capital: Record<string, number>; max: number;
  cost: Record<string, Partial<Record<string, number>>>; seconds: Record<string, number>; kitPrice: Record<string, Partial<Record<string, number>>>; starting: Record<string, number>;
}
export async function fetchExpeditionStock(): Promise<ExpeditionStock | null> {
  try { const res = await fetch('/api/expedition/stock', { headers: authHeaders() }); return res.ok ? ((await res.json()) as ExpeditionStock) : null; } catch { return null; }
}
/** What the Colony knows of a wreck (0 it is there … 3 the General has read it) and the loadout the Général suggests. */
export async function fetchExpeditionIntel(system: string, poi: string, difficulty: 'normal' | 'hard' = 'normal'): Promise<{ intel: rt.Intel; loadout: rt.Loadout } | null> {
  try { const res = await fetch(`/api/expedition/intel?system=${encodeURIComponent(system)}&poi=${encodeURIComponent(poi)}&difficulty=${difficulty}`, { headers: authHeaders() }); return res.ok ? ((await res.json()) as { intel: rt.Intel; loadout: rt.Loadout }) : null; } catch { return null; }
}
/** Forms troops at the capital: the new stock, or the refusal (`insufficient` with `missing`, `queue_full`, `too_many`…). */
export async function trainExpeditionTroops(unit: rt.UnitType, count: number): Promise<{ ok: true; stock: ExpeditionStock } | { ok: false; error: string; missing?: Record<string, number> }> {
  try {
    const res = await fetch('/api/expedition/troops', { method: 'POST', headers: authHeaders(), body: JSON.stringify({ unit, count }) });
    const j = await res.json().catch(() => ({})) as { error?: string; missing?: Record<string, number> } & Partial<ExpeditionStock>;
    return res.ok ? { ok: true, stock: j as ExpeditionStock } : { ok: false, error: j.error ?? `http_${res.status}`, ...(j.missing ? { missing: j.missing } : {}) };
  } catch { return { ok: false, error: 'offline' }; }
}
export const expeditionAct = (action: string): Promise<ExpeditionReply> => expeditionCall('/act', { action });
export async function abandonExpedition(): Promise<boolean> {
  try { return (await fetch('/api/expedition/abandon', { method: 'POST', headers: authHeaders(), body: '{}' })).ok; } catch { return false; }
}
